import { Modal } from '@fozito/common-ui';
import { Form } from 'antd';
import { delay, set } from 'lodash';
import { PAYMENT_TYPE } from 'modules/POS/Cashier/cashier.constants';
import { IUICashierFormValues } from 'modules/POS/Cashier/cashier.interface';
import { useSocketContext } from 'modules/POS/Cashier/hooks/useSocket';
import { ICustomerPaymentDetail, PaymentSocketData, TypePaymentActors, TypePaymentPassData } from 'modules/POS/Cashier/socket.interface';
import cashierApis from 'modules/POS/services/apis';
import { IBillInfoResData } from 'modules/POS/services/types/bill';
import { ICashierCheckPayment, ICashierPaymentCheckInfo } from 'modules/POS/services/types/payment';
import moment from 'moment';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { IResponseData } from 'services/response';
import { parseDecimal } from 'utils/number';
import storage from 'utils/sessionStorage';
import { PAYMENT_PASS_DATA_TOPIC } from 'utils/socket';
import CheckWithSignatureForm from './CheckWithSignatureForm';
import ModalCheckInfo, { IFormCheckInfoDone, IFormCheckValue, useModalCheckInfoRef } from './ModalInfo';
import { useCheckSignature } from './Signature';
import cashierService from 'modules/POS/services';
import { Message } from 'stompjs';
import { cashierCtrl } from 'modules/POS/Cashier/cashier.controller';
import useListenRequestSync from 'modules/POS/Cashier/hooks/useListenRequestSync';

export type ICashierCheckPaymentMethod = (billId: string, payment: ICashierCheckPayment) => Promise<boolean>;
type Props = {
  onPayment: ICashierCheckPaymentMethod;
  cashIncentive?: IUICashierFormValues['cashIncentive'];
  cashIncentiveAmount?: IUICashierFormValues['cashIncentiveAmount'],
  enable?: IUICashierFormValues['cashIncentiveDiscount'],
};
type Ref = {
  open: (billId: string) => void;
};
export const useModalCheckPaymentRef = () => useRef<Ref>(null);
const ModalCheckPayment = forwardRef<Ref, Props>(({ onPayment, cashIncentive, enable, cashIncentiveAmount }, ref) => {
  const checkRef = useCheckSignature();
  const [form] = Form.useForm();
  const infoRef = useModalCheckInfoRef();
  const socketContext = useSocketContext();
  const cashierMsg = cashierService.selectors.cashierMsg();

  const [open, setOpen] = useState(false);
  const [billId, setBillId] = useState('');
  const onClose = () => setOpen(false);
  const onCancel = () => {
    onClose();
    socketContext.backToCheckIn();
  };


  const sendingData = (type: string, data?: any) => {
    const body: PaymentSocketData = {
      storeId: storage.shop_id.get(),
      billId: billId || '',
      actor: TypePaymentActors.CASHIER,
      data,
    };
    switch (type) {
      case TypePaymentPassData.REQUEST_SIGNATURE_CHECK: {
        set(body, 'action', TypePaymentPassData.REQUEST_SIGNATURE_CHECK);
        break;
      }
      case TypePaymentPassData.COMPLETE_SIGNATURE_CHECK: {
        set(body, 'action', TypePaymentPassData.COMPLETE_SIGNATURE_CHECK);
        break;
      }
      default:
        break;
    }

    socketContext.send(PAYMENT_PASS_DATA_TOPIC, body);
  };
  const listeningData = (message: Message) => {
    if (!message.body) return;
    const payment: PaymentSocketData = JSON.parse(message.body);
    if (!payment) return;
    if (payment.billId !== billId) return;
    if (payment.actor !== TypePaymentActors.CUSTOMER) return;

    switch (payment.action) {
      case TypePaymentPassData.RECEIVE_SIGNATURE_CHECK: {
        checkRef.current?.setValue(payment.data.signature);
        form.setFieldValue('signature', payment.data.signature);
        delay(() => form.submit(), 200);
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (!cashierMsg) return;
    listeningData(cashierMsg);
  }, [cashierMsg]);

  const getDiscount = (subTotal: number) => {
    if (!cashIncentive) return 0;
    const { discountType, discountValue } = cashIncentive;
    if (discountType === 'PERCENT') {
      return Math.round(subTotal * (discountValue / 100));
    }
    if (discountType === 'MONEY')
      return parseDecimal(discountValue);
    return 0;
  };

  const fetchDetail = async (_billId: string) => {
    try {
      const res: IResponseData<IBillInfoResData> = await cashierApis.getBillById(_billId);
      const detail = res.data.data;
      if (detail) {
        cashierCtrl.switchTicketWithDetailData(detail);
        const finalTotal = detail.total - (cashIncentiveAmount || 0);
        form.resetFields();
        form.setFieldValue('detail', detail);
        form.setFieldValue('amount', finalTotal);

        socketContext.send(PAYMENT_PASS_DATA_TOPIC, {
          billId,
          actor: TypePaymentActors.CASHIER,
          action: TypePaymentPassData.PASS_DATA,
          data: {
            cash: 0,
            remaining: 0,
            chanceCash: 0,
            loyalty: 0,
            giftCard: 0,
            checkOthers: finalTotal,
            check: finalTotal,
            card: 0,
            multiPay: 0,
            totalValue: finalTotal,
            subTotal: detail.subTotal || 0,
            cardFee: 0,
            saleTax: detail.saleTax || 0,
            usedTax: detail.useTax || 0,
            discount: (detail.discount || 0) + (detail.discountItem || 0) + (cashIncentiveAmount || 0),
          } as ICustomerPaymentDetail,
        });
        return detail;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  useListenRequestSync(async (_billId) => {
    if (!billId) return;
    await fetchDetail(_billId);
  });

  const onOpen: Ref['open'] = async (_billId) => {
    setBillId(_billId);
    socketContext.switchCusPayment(_billId);
    try {
      const detail = await fetchDetail(_billId);
      if (detail) {
        const finalTotal = detail.total - (cashIncentiveAmount || 0);
        infoRef.current?.open(detail, finalTotal);
      }
    } catch (error) {

    }
  };

  const handleFinish = async (values: any) => {
    const data = values as {
      amount: number,
      detail: IBillInfoResData,
      checkInfo: ICashierPaymentCheckInfo,
      formInfoValue: IFormCheckValue,
      date: string,
    };

    const billId = data.detail.billId;
    if (!billId) return;
    const payment: ICashierCheckPayment = {
      amount: data.amount,
      paymentType: PAYMENT_TYPE.CHECK,
      checkInfo: data.checkInfo,
      cashIncentiveDiscount: enable,
      discountIncentive: enable ? getDiscount(data.detail.subTotal) : 0,
    };
    try {
      const isSuccess = await onPayment(billId, payment);
      if (!isSuccess) return;
      sendingData(TypePaymentPassData.COMPLETE_SIGNATURE_CHECK);
      socketContext.backToCheckIn();
      onClose();
    } catch (error) {
    } finally {
    }
  };

  const onCheckInfoDone: IFormCheckInfoDone = (formInfoValue, checkInfo) => {
    const values = { formInfoValue, checkInfo, date: moment().format('MM/DD/YYYY') };
    form.setFieldsValue(values);
    sendingData(TypePaymentPassData.REQUEST_SIGNATURE_CHECK, values);
    setOpen(true);
  };

  useImperativeHandle(ref, () => ({
    open: onOpen
  }));

  return (
    <>
      <ModalCheckInfo ref={infoRef} onDone={onCheckInfoDone} onCancel={onCancel} />
      <Modal open={open} title='check ticket' onClose={onCancel} styles={{
        content: { height: '100vh', boxShadow: 'none' },
        header: { display: 'none' },
        body: { height: '100vh', maxHeight: 'unset', display: 'flex' },
        wrapper: { background: '#fff' },
        mask: { display: 'none' }
      }} width={'100vw'}>
        <CheckWithSignatureForm checkRef={checkRef} form={form} onFinish={handleFinish} onClose={onCancel} />
      </Modal>
    </>
  );
});
ModalCheckPayment.displayName = 'ModalCheckPayment';
export default ModalCheckPayment;